/* eslint-disable enforce-ids-in-jsx/missing-ids */
import React, { useMemo } from 'react'

import Checkbox from 'components/lib/DesignSystem/Checkbox'
import { PSIMA_PENDO_FEATURES } from 'components/pages/SpearPhishingAgent/psimaPendo'

import {
  Card,
  CardDescription,
  CardHeaderCheckboxContainer,
  CardHeaderContent,
  CardHeaderTitle,
  CardMainContent,
  CardMainLeft,
  CardMainRight,
  CardWrapper,
  DetailsContainer,
  VisibleDetailsContainer,
} from '../styles'
import { getSourcesByKind, SOURCE_KIND_ORDER } from '../utils'

import BottomDetails from './BottomDetails'
import DateDetails from './DateDetails'
import EmptyAudienceNote from './EmptyAudienceNote'
import FeedbackRating from './FeedbackRating'
import getParticipantColumns from './participantColumns'
import ParticipantsSection from './ParticipantsSection'
import SimulationBlueprint from './SimulationBlueprint'
import TriggerDetails from './TriggerDetails'

const CampaignCard = ({
  campaign,
  isExpanded,
  isSelected,
  isMobile,
  isExpired,
  participantsData,
  excludedParticipantsForCampaign,
  getParticipantDuplicateState,
  setExcludedParticipant,
  toggleAllParticipants,
  onClaimAllParticipants,
  onToggleDetails,
  onSelectCampaign,
  onLaunchSimulation,
}) => {
  const {
    id,
    title,
    level,
    audience,
    createdAt,
    expiresAt,
    sources,
    behavioralRiskLevel,
    data: { description, threat, impersonation, narrative },
  } = campaign

  const columns = useMemo(
    () =>
      getParticipantColumns({
        campaignId: id,
        participantIds: participantsData.map((participant) => participant.id),
        excludedParticipantIds: excludedParticipantsForCampaign,
        setExcludedParticipant,
        toggleAllParticipants,
        getParticipantDuplicateState,
      }),
    [
      id,
      participantsData,
      excludedParticipantsForCampaign,
      setExcludedParticipant,
      toggleAllParticipants,
      getParticipantDuplicateState,
    ]
  )

  const sourcesByKind = getSourcesByKind(sources)
  const sourceKindsToRender = SOURCE_KIND_ORDER.filter(
    (sourceKind) => sourcesByKind[sourceKind].length > 0
  )
  const departments = Array.from(
    new Set(audience.searchKeys.map(({ department }) => department).filter(Boolean))
  ).join(', ')
  const behavioralRiskPercentage = Math.round(behavioralRiskLevel * 100)
  const participantsCount =
    audience.suggestedProfiles.length - excludedParticipantsForCampaign.length
  const hasExcludedDuplicates = participantsData.some(
    ({ id: participantId }) => getParticipantDuplicateState(id, participantId) === 'loser'
  )
  const isLaunchDisabled = excludedParticipantsForCampaign.length === participantsData.length
  const isEmpty =
    isSelected &&
    participantsData.length > 0 &&
    excludedParticipantsForCampaign.length === participantsData.length

  return (
    <Card
      $expanded={isExpanded}
      data-testid={`Psima_Dashboard_div-${id}`}
      id={`psima-dashboard-card-${id}`}
    >
      <VisibleDetailsContainer
        data-testid={`Psima_Dashboard_VisibleDetailsContainer_div-${id}`}
        onClick={() => onToggleDetails(id)}
        $expanded={isExpanded}
      >
        <CardWrapper>
          <div style={{ width: isMobile ? 'calc(100% - 30px)' : '100%' }}>
            <CardMainContent>
              <CardMainLeft>
                <CardHeaderContent>
                  <CardHeaderCheckboxContainer onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      id={`Psima_Dashboard_Campaign_checkbox-${id}`}
                      dataTestId={`Psima_Dashboard_checkbox-${id}`}
                      dataPendoFeature={PSIMA_PENDO_FEATURES.DASHBOARD_CARD_SELECT}
                      checked={isSelected && !isEmpty}
                      onChange={(e) => {
                        e.stopPropagation()

                        if (isEmpty) {
                          onClaimAllParticipants(id)
                          return
                        }

                        onSelectCampaign(id)
                      }}
                    />
                  </CardHeaderCheckboxContainer>
                  <CardHeaderTitle data-testid={`Psima_Dashboard_Name_div-${id}`}>
                    {title}
                  </CardHeaderTitle>
                </CardHeaderContent>
                <CardDescription
                  id={`Psima_Dashboard_Description_div-${id}`}
                  data-testid={`Psima_Dashboard_Description_div-${id}`}
                  title={description}
                >
                  {description}
                </CardDescription>
                <TriggerDetails
                  campaignId={id}
                  level={level}
                  sourcesByKind={sourcesByKind}
                  sourceKindsToRender={sourceKindsToRender}
                />
                {isEmpty ? (
                  <EmptyAudienceNote campaignId={id} />
                ) : (
                  <BottomDetails
                    campaignId={id}
                    employeesCount={participantsCount}
                    hasExcludedDuplicates={hasExcludedDuplicates}
                    behavioralRiskPercentage={behavioralRiskPercentage}
                    departments={departments}
                  />
                )}
              </CardMainLeft>
              <CardMainRight>
                <DateDetails
                  campaignId={id}
                  createdAt={createdAt}
                  expiresAt={expiresAt}
                  isExpired={isExpired}
                />
                <FeedbackRating cardId={id} />
              </CardMainRight>
            </CardMainContent>
          </div>
        </CardWrapper>
      </VisibleDetailsContainer>
      {isExpanded && (
        <DetailsContainer data-testid={`Psima_Dashboard_Details_div-${id}`}>
          <SimulationBlueprint
            campaignId={id}
            threat={threat}
            impersonation={impersonation}
            narrative={narrative}
          />
          <ParticipantsSection
            campaignId={id}
            participantsCount={participantsCount}
            columns={columns}
            participantsData={participantsData}
            getParticipantDuplicateState={getParticipantDuplicateState}
            isLaunchDisabled={isLaunchDisabled}
            onLaunchSimulation={onLaunchSimulation}
          />
        </DetailsContainer>
      )}
    </Card>
  )
}

export default CampaignCard
